import React, { useCallback, useMemo, useState } from "react";
import styled from "@emotion/styled/macro";
import {
  Button,
  LoadingIndicator,
  Snackbar,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
  UtilityConnectionContainer,
  WizardCard,
  useSingletonMobileMediaQuery,
} from "@sunrun/experience-ui-components";
import { SplatCheckoutDocument } from "../../../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { CheckoutFooter } from "../../molecules/CheckoutFooter";
import { CheckoutUtilityBillSlideout } from "../CheckoutUtilityBillSlideout";
import { CheckoutCustomerInfoSlideout } from "../CheckoutCustomerInfoSlideout";
import {
  CUSTOMER_INFO_LABELS,
  DEFAULT_PREFERRED_LANGUAGE,
  getAdditionalSystemLabel,
} from "./constants";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useCheckoutFooter } from "checkout/providers/CheckoutFooterContext";
import { useProspect } from "checkout/hooks/useCheckoutSwr";
import { useAuthToken } from "hooks/useAuthToken";
import { useGetUtilityConnectionUrl } from "hooks/useGetUtilityConnectionUrl";
import { useGetUtilityConnectionStatus } from "hooks/useGetUtilityConnectionStatus";
import { ResignRiskModal } from "checkout/components/molecules/ResignRiskModal";

const steps = [
  {
    title: "Customer info",
    testId: "customer-info-step",
  },
  {
    title: "Utility bill",
    testId: "utility-bill-step",
  },
];

const ConfirmHomeInfoTaskView: React.FC = () => {
  const {
    navigateToNextTask,
    hasUploadedUtilityBill,
    utilityBillAttachments,
    isUtilityBillAttachmentsLoading,
    prospectId,
    hasAdditionalSystem,
  } = useCheckoutContext();
  const { data: prospect } = useProspect(prospectId);
  const authKey = useAuthToken("HYBRID")?.token || "";
  const [wizardCurrentStep, setWizardCurrentStep] = useState(0);
  const [
    isMissingUtilityBillDestructiveModalOpen,
    setIsMissingUtilityBillDestructiveModalOpen,
  ] = useState(false);
  const [isUtilityBillSlideoutOpen, setIsUtilityBillSlideoutOpen] =
    useState(false);
  const [isCustomerInfoSlideoutOpen, setIsCustomerInfoSlideoutOpen] =
    useState(false);
  const [showGetBillOnlineIframe, setShowGetBillOnlineIframe] = useState(false);
  const [showUploadSuccessToast, setShowUploadSuccessToast] = useState(false);
  const [showUploadBillInfoSnackbar, setShowUploadBillInfoSnackbar] =
    useState(false);

  const {
    getUrl,
    loading: billOnlineLoading,
    url: billOnlineUrl,
  } = useGetUtilityConnectionUrl(authKey);
  const {
    status: intervalDataStatus,
    initialLoading: intervalDataInitialLoading,
    startPolling,
  } = useGetUtilityConnectionStatus(authKey, prospectId || null);

  const languagePreference = useMemo(() => {
    return prospect?.preferredLanguage || DEFAULT_PREFERRED_LANGUAGE;
  }, [prospect?.preferredLanguage]);

  const closeMissingUtilityBillDestructiveModal = useCallback(() => {
    setIsMissingUtilityBillDestructiveModalOpen(false);
  }, []);

  const handleConfirmHomeInfo = useCallback(() => {
    if (!hasUploadedUtilityBill) {
      setIsMissingUtilityBillDestructiveModalOpen(true);
      return;
    }
    navigateToNextTask();
  }, [hasUploadedUtilityBill, navigateToNextTask]);

  const handleUploadBillFromDestructiveModal = useCallback(() => {
    closeMissingUtilityBillDestructiveModal();
    setIsUtilityBillSlideoutOpen(true);
    setShowUploadBillInfoSnackbar(true);
  }, [closeMissingUtilityBillDestructiveModal]);

  const handleRiskResigning = useCallback(() => {
    closeMissingUtilityBillDestructiveModal();
    navigateToNextTask();
  }, [closeMissingUtilityBillDestructiveModal, navigateToNextTask]);

  const onGetBillOnline = useCallback(() => {
    if (prospectId && !intervalDataStatus) {
      getUrl(prospectId);
      setShowGetBillOnlineIframe(true);
    }
  }, [prospectId, intervalDataStatus, getUrl]);

  useCheckoutFooter(
    <CheckoutFooter
      buttonText="Confirm Home Info"
      onClick={handleConfirmHomeInfo}
      isDisabled={wizardCurrentStep === 0}
    />,
    [handleConfirmHomeInfo, wizardCurrentStep]
  );

  return (
    <>
      {isMissingUtilityBillDestructiveModalOpen && (
        <ResignRiskModal
          testId="utility-bill-missing-modal"
          title="Name on utility bill has to match NEM signer"
          body={[
            "The NEM Agreement needs to be signed by the customer whose name is on the utility bill.",
            "Without a utility bill you may sign with the wrong name and the customer will have to re-sign documents.",
          ]}
          acknowledgeTestId="utility-bill-missing-acknowledge-checkbox"
          primaryButtonText="Go back & upload bill"
          onPrimaryClick={handleUploadBillFromDestructiveModal}
          onRiskResigning={handleRiskResigning}
          onClose={closeMissingUtilityBillDestructiveModal}
        />
      )}
      {isCustomerInfoSlideoutOpen && (
        <CheckoutCustomerInfoSlideout
          onClose={() => setIsCustomerInfoSlideoutOpen(false)}
        />
      )}
      {isUtilityBillSlideoutOpen && (
        <CheckoutUtilityBillSlideout
          onClose={() => setIsUtilityBillSlideoutOpen(false)}
          onGetBillOnline={onGetBillOnline}
          onUploadSuccess={() => setShowUploadSuccessToast(true)}
          intervalDataInitialLoading={intervalDataInitialLoading}
          intervalDataStatus={intervalDataStatus}
        />
      )}
      {showGetBillOnlineIframe && (
        <UtilityConnectionContainer
          src={billOnlineUrl || ""}
          isLoading={billOnlineLoading}
          onClose={() => {
            setShowGetBillOnlineIframe(false);
            setIsUtilityBillSlideoutOpen(true);
            startPolling();
          }}
          onError={() => setShowGetBillOnlineIframe(false)}
          returnButtonText="Back"
        />
      )}
      <SnackbarOverlay>
        <Snackbar
          type="info"
          colorScheme="dark"
          open={showUploadBillInfoSnackbar}
          onClose={() => setShowUploadBillInfoSnackbar(false)}
          autoHideDuration={10000}
          message='You clicked "Go back & upload bill" so that we can avoid the customer having to re-sign NEM. If you want to proceed to signing without a bill, click "Confirm Home Info", then "Proceed & risk re-signing"'
        />
        <Snackbar
          type="success"
          colorScheme="dark"
          open={showUploadSuccessToast}
          onClose={() => setShowUploadSuccessToast(false)}
          message="Utility bill uploaded successfully"
        />
      </SnackbarOverlay>
      <WizardCard
        steps={steps}
        currentStep={wizardCurrentStep}
        onBack={() => setWizardCurrentStep(0)}
        primaryAction={
          wizardCurrentStep === 0
            ? {
                label: "Confirm customer info",
                onClick: () => setWizardCurrentStep(1),
              }
            : undefined
        }
      >
        <StepContent>
          {wizardCurrentStep === 0 ? (
            <CustomerInfoStep
              languagePreference={languagePreference}
              hasAdditionalSystem={hasAdditionalSystem}
              utilityCompany={prospect?.utilityCompany || ""}
              homeType={prospect?.homeType || ""}
              onEditCustomerInfo={() => setIsCustomerInfoSlideoutOpen(true)}
            />
          ) : (
            <UtilityBillStep
              hasUploadedUtilityBill={!!hasUploadedUtilityBill}
              utilityBillAttachments={utilityBillAttachments}
              isUtilityBillAttachmentsLoading={isUtilityBillAttachmentsLoading}
              onOpenSlideout={() => setIsUtilityBillSlideoutOpen(true)}
            />
          )}
        </StepContent>
      </WizardCard>
    </>
  );
};

const CustomerInfoStep: React.FC<{
  languagePreference: string;
  hasAdditionalSystem: boolean;
  utilityCompany: string;
  homeType: string;
  onEditCustomerInfo: () => void;
}> = ({
  languagePreference,
  hasAdditionalSystem,
  utilityCompany,
  homeType,
  onEditCustomerInfo,
}) => {
  const isMobile = useSingletonMobileMediaQuery();

  if (isMobile) {
    return (
      <Fields gap={16}>
        <InfoField
          label={CUSTOMER_INFO_LABELS.language}
          value={languagePreference || DEFAULT_PREFERRED_LANGUAGE}
          testId="customer-info-language"
          size="sm"
          isMobile
        />
        <InfoField
          label={CUSTOMER_INFO_LABELS.solarPanels}
          value={getAdditionalSystemLabel(hasAdditionalSystem)}
          testId="customer-info-solar"
          size="sm"
          isMobile
        />
        <InfoField
          label={CUSTOMER_INFO_LABELS.utilityCompany}
          value={utilityCompany}
          testId="customer-info-utility-company"
          size="sm"
          isMobile
        />
        <InfoField
          label={CUSTOMER_INFO_LABELS.homeType}
          value={homeType}
          testId="customer-info-home-type"
          size="sm"
          isMobile
        />
        <FullWidthButton
          color="secondary"
          size="sm"
          customTestId="customer-info-edit"
          onClick={onEditCustomerInfo}
        >
          Edit customer info
        </FullWidthButton>
      </Fields>
    );
  }

  return (
    <Fields gap={26}>
      <LanguageRow>
        <InfoField
          label={CUSTOMER_INFO_LABELS.language}
          value={languagePreference || DEFAULT_PREFERRED_LANGUAGE}
          testId="customer-info-language"
          size="sm"
        />
        <Button
          color="secondary"
          size="sm"
          customTestId="customer-info-edit"
          onClick={onEditCustomerInfo}
        >
          Edit customer info
        </Button>
      </LanguageRow>
      <InfoField
        label={CUSTOMER_INFO_LABELS.solarPanels}
        value={getAdditionalSystemLabel(hasAdditionalSystem)}
        testId="customer-info-solar"
        size="sm"
      />
      <SideBySideFields>
        <InfoField
          label={CUSTOMER_INFO_LABELS.utilityCompany}
          value={utilityCompany}
          testId="customer-info-utility-company"
          size="sm"
        />
        <InfoField
          label={CUSTOMER_INFO_LABELS.homeType}
          value={homeType}
          testId="customer-info-home-type"
          size="sm"
        />
      </SideBySideFields>
    </Fields>
  );
};

const InfoField: React.FC<{
  label: string;
  value: string;
  testId: string;
  size: "sm" | "md";
  isMobile?: boolean;
}> = ({ label, value, testId, size, isMobile }) => (
  <FieldContainer>
    <Typography
      color={tokens.BRAND_HERO_BLUE}
      size={isMobile ? tokens.FONT_SIZE_1 : tokens.FONT_SIZE_3}
      fontWeight="medium"
    >
      {label}
    </Typography>
    <ReadOnlyValueButton
      color="primary"
      size={size}
      customTestId={testId}
      tabIndex={-1}
      aria-disabled={true}
    >
      {value}
    </ReadOnlyValueButton>
  </FieldContainer>
);

const MAX_BILL_FILE_NAME_LENGTH = 50;

const truncateFileName = (name: string) =>
  name.length > MAX_BILL_FILE_NAME_LENGTH
    ? `${name.slice(0, MAX_BILL_FILE_NAME_LENGTH)}…`
    : name;

const UtilityBillFileList: React.FC<{
  attachments: SplatCheckoutDocument[];
}> = ({ attachments }) => (
  <FileList data-testid="utility-bill-file-list">
    {attachments.map((attachment) => (
      <FileLink
        key={attachment.documentId}
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        title={attachment.documentName}
        data-testid="utility-bill-file-link"
      >
        <SvgIcon
          name={SvgNames.File}
          color={tokens.BRAND_HERO_BLUE}
          width={16}
          height={20}
        />
        <Typography
          color={tokens.BRAND_HERO_BLUE}
          size={tokens.FONT_SIZE_1}
          fontWeight="medium"
        >
          {truncateFileName(attachment.documentName)}
        </Typography>
      </FileLink>
    ))}
  </FileList>
);

const UtilityBillStep: React.FC<{
  hasUploadedUtilityBill: boolean;
  utilityBillAttachments: SplatCheckoutDocument[];
  isUtilityBillAttachmentsLoading: boolean;
  onOpenSlideout: () => void;
}> = ({
  hasUploadedUtilityBill,
  utilityBillAttachments,
  isUtilityBillAttachmentsLoading,
  onOpenSlideout,
}) => {
  const isMobile = useSingletonMobileMediaQuery();

  return (
    <>
      {hasUploadedUtilityBill && isUtilityBillAttachmentsLoading ? (
        <LoadingCard data-testid="utility-bill-uploaded-card-loading">
          <LoadingIndicator color="black" showLoadingMessage={false} />
        </LoadingCard>
      ) : hasUploadedUtilityBill ? (
        <UploadedCard data-testid="utility-bill-uploaded-card">
          <UpdateButton
            color="secondary"
            size="sm"
            customTestId="utility-bill-update"
            onClick={onOpenSlideout}
          >
            Update
          </UpdateButton>
          <SvgIcon
            name={SvgNames.CheckEnclosed}
            color={tokens.SUCCESS_50}
            width={56}
            height={56}
            testId="utility-bill-uploaded-icon"
          />
          <Typography
            color={tokens.BLACK}
            size={tokens.FONT_SIZE_6}
            fontWeight="regular"
          >
            Utility bill uploaded
          </Typography>
          <UtilityBillFileList attachments={utilityBillAttachments} />
        </UploadedCard>
      ) : (
        <UploadPrompt>
          <Typography
            color={tokens.BRAND_HERO_BLUE}
            size={isMobile ? tokens.FONT_SIZE_1 : tokens.FONT_SIZE_3}
            fontWeight="medium"
          >
            We strongly recommend uploading the utility bill or getting the bill
            online now. This will prevent customer from having to re-sign NEM.
          </Typography>
          <ButtonRow isMobile={isMobile}>
            <FullWidthButton
              color="secondary"
              size={isMobile ? "sm" : "md"}
              customTestId="utility-bill-upload"
              iconRight={
                <SvgIcon
                  name={SvgNames.Upload}
                  color={tokens.BRAND_HERO_BLUE}
                  width={24}
                  height={24}
                />
              }
              onClick={onOpenSlideout}
            >
              Upload
            </FullWidthButton>
            <FullWidthButton
              color="primary"
              customTestId="utility-bill-get-online"
              size={isMobile ? "sm" : "md"}
              iconRight={
                <SvgIcon
                  name={SvgNames.ArrowSquareOutSvg}
                  color={tokens.WHITE}
                  width={24}
                  height={24}
                />
              }
              onClick={onOpenSlideout}
            >
              Get bill online
            </FullWidthButton>
          </ButtonRow>
        </UploadPrompt>
      )}
    </>
  );
};

const StepContent = styled.div`
  width: 100%;
`;

const Fields = styled.div<{ gap: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap }) => `${gap}px`};
  width: 100%;
`;

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LanguageRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
`;

const SideBySideFields = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 48px;
  width: 100%;
`;

const ReadOnlyValueButton = styled(Button)`
  background-color: ${tokens.BRAND_MEDIUM_BLUE};
  cursor: not-allowed;
  opacity: 1;
  width: fit-content;

  &:hover {
    opacity: 1;
    cursor: not-allowed;
  }
`;

const UploadPrompt = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const ButtonRow = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? "column" : "row")};
  gap: ${({ isMobile }) => (isMobile ? "16px" : "26px")};
  width: 100%;
`;

const FullWidthButton = styled(Button)`
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
`;

const UploadedCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  padding: 24px;
  overflow: clip;
  background: ${tokens.TINTS_OFF_WHITE_80};
  border: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  border-radius: 8px;
`;

const LoadingCard = styled(UploadedCard)`
  align-items: center;
  justify-content: center;
  min-height: 160px;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const FileLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  text-decoration: underline;
  text-decoration-color: ${tokens.BRAND_HERO_BLUE};
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const UpdateButton = styled(Button)`
  position: absolute;
  top: 16px;
  right: 16px;
`;

const SnackbarOverlay = styled.div`
  position: relative;
  z-index: 10000;
`;

export { ConfirmHomeInfoTaskView };
